import { useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import type { DebouncedFunc } from "lodash";

import { CepAddress, fetchAddressByCep, onlyDigits } from "@/Utils";

export const CEP_LOOKUP_DELAY = 500;

type Lookup = (cep: string) => Promise<void>;

export const useCepLookup = (onFound: (address: CepAddress) => void) => {
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const onFoundRef = useRef(onFound);
  useEffect(() => {
    onFoundRef.current = onFound;
  }, [onFound]);

  const requestRef = useRef(0);
  const debouncedRef = useRef<DebouncedFunc<Lookup> | null>(null);

  useEffect(() => {
    const debounced = debounce(async (cep: string) => {
      const request = ++requestRef.current;

      const address = await fetchAddressByCep(cep);

      if (request !== requestRef.current) return;
      setIsLoading(false);
      setNotFound(!address);
      if (address) onFoundRef.current(address);
    }, CEP_LOOKUP_DELAY);

    debouncedRef.current = debounced;

    return () => {
      debounced.cancel();
      debouncedRef.current = null;
      requestRef.current += 1;
    };
  }, []);

  const lookup = (cep: string) => {
    const debounced = debouncedRef.current;
    if (!debounced) return;

    if (onlyDigits(cep).length !== 8) {
      debounced.cancel();
      requestRef.current += 1;
      setIsLoading(false);
      setNotFound(false);
      return;
    }

    setIsLoading(true);
    setNotFound(false);
    debounced(cep);
  };

  return { lookup, isLoading, notFound };
};
